import {booleanAttribute, Component, computed, input} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {AvatarComponent} from '../avatar/avatar.component';
import {StatusBadgeComponent} from '../status-badge/status-badge.component';
import {TaskStatusTone} from '../task-row/task-row.component';
import {Tone, Vote} from '../ui-colors';

export interface PrReviewer {
    name: string;
    /** Avatar color seed, preferably the CRM user id so the colors match the colleague list. */
    seed?: number | string | null;
    vote: Vote;
}

export interface PrWorkItem {
    id: number | string;
    type: string;
    state: string;
    stateTone: TaskStatusTone;
    title: string;
}

const MERGE_STATUS: Record<string, { tone: Tone; label: string }> = {
    succeeded: {tone: 'ok', label: 'Merge mogelijk'},
    pending: {tone: 'warn', label: 'Merge wordt gecontroleerd'},
    queued: {tone: 'warn', label: 'Merge wordt gecontroleerd'},
    conflicts: {tone: 'error', label: 'Merge-conflicten'},
    failure: {tone: 'error', label: 'Merge mislukt'},
    rejectedByPolicy: {tone: 'error', label: 'Merge geweigerd door policy'},
};

/**
 * A pull request with its reviewers (avatar border = vote).
 * `row`: title, "!8172 · ma 12 okt, 11:05" and the reviewers on the right (daily).
 * `card`: title, repo · branch → target, status, time, reviewers and the linked work items (weekly).
 * The whole card links to `href`; context menus are bound by the page on the host.
 */
@Component({
    selector: 'app-pr-card',
    imports: [NgTemplateOutlet, AvatarComponent, StatusBadgeComponent],
    templateUrl: './pr-card.component.html',
    styleUrl: './pr-card.component.scss',
    host: {
        '[class]': '"is-" + variant()',
        '[class.is-draft]': 'draft()',
        '[attr.title]': 'null'
    }
})
export class PrCardComponent {
    readonly variant = input<'row' | 'card'>('row');
    readonly title = input<string>('');
    readonly href = input<string | null>(null);
    readonly number = input<number | string | null>(null);
    readonly repo = input<string>('');
    readonly branch = input<string>('');
    readonly target = input<string>('');
    readonly status = input<string>('');
    readonly statusTone = input<Tone>('neutral');
    /** Preformatted time, e.g. "ma 12 okt, 11:05". */
    readonly when = input<string>('');
    readonly reviewers = input<PrReviewer[]>([]);
    readonly workItems = input<PrWorkItem[]>([]);
    readonly draft = input(false, {transform: booleanAttribute});
    /** Azure merge status (succeeded, conflicts, pending, …): colored merge icon in the row variant. */
    readonly mergeStatus = input<string | null>(null);

    readonly branchLine = computed(() => [this.repo(), this.branch()].filter(Boolean).join(' · ') + (this.target() ? ` → ${this.target()}` : ''));
    readonly merge = computed(() => MERGE_STATUS[this.mergeStatus() ?? ''] ?? null);
}
